/** @format */

import React, {useEffect} from 'react';

import classNames from 'classnames';
import {useOptionallyControlledState} from '../hooks/use_optionally_controlled_state';

export function Tabs({vertical, activeTab, initialTab, onChange, children, ...props}) {
    const tabsClass = classNames('tabs', 'mb-5', {'tabs--vertical': vertical});

    const tabNames = React.Children.map(children, (child, i) => child.props.name ?? i);

    const [activeTabName, setActiveTabName] = useOptionallyControlledState(
        activeTab,
        initialTab ?? tabNames[0],
        handleChange
    );

    const tabs = React.Children.map(children, (child, i) => {
        const tabName = tabNames[i];
        return React.cloneElement(child, {
            name: tabName,
            active: tabName === activeTabName,
            onClick: () => setActiveTabName(tabName)
        });
    });

    function handleChange(tabName) {
        if (onChange) {
            const child = findTab(tabName);
            onChange(tabName, {...child.props});
        }
    }

    function findTab(tabName) {
        return tabs.find(tab => tab.props.name === tabName) || tabs[0];
    }

    const activeTabElement = findTab(activeTabName);

    return (
        <>
            <ul className={tabsClass} role="tablist" data-testid="tabs" {...props}>
                {tabs}
            </ul>
            {activeTabElement.props.children && (
                <div role="tabpanel">{activeTabElement.props.children}</div>
            )}
        </>
    );
}

Tabs.defaultProps = {
    vertical: false
};

export function Tab({title, active, disabled, onClick, name}) {
    const tabClass = classNames('tab', {
        'tab--active color-action-secondary': active,
        'tab--disabled': disabled
    });
    return (
        <li>
            <button
                className={tabClass}
                role="tab"
                data-name={name}
                disabled={disabled}
                onClick={onClick}
                aria-selected={active}
                data-testid="tab">
                <span>{title}</span>
            </button>
        </li>
    );
}

Tab.defaultProps = {
    active: false,
    disabled: false
};
